import {
  CommandBarButton,
  IColumn,
  Icon,
  MarqueeSelection,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  SearchBox,
  Selection,
  ShimmeredDetailsList,
  Spinner,
} from '@fluentui/react';
import { Patient } from '../../api';
import { InfoDialog } from '../../components/core/InfoDialog';
import { DOCUMENT_TYPE } from '../../config/constants';
import { HealAPI } from '../../healApi';
import moment from 'moment-timezone';
import * as React from 'react';
import Dropzone from 'react-dropzone';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { age } from '../../util/Date';
import { AsyncData } from '../../redux/core';
import { toast } from '../../redux/message/actions';
import { ToastParams } from '../../redux/message/types';
import {
  addPatientFile,
  approvePatientFile,
  approvePatientFiles,
  deletePatientFiles,
  fetchPatientFiles,
  releasePatientFiles,
  updatePatientFiles,
} from '../../redux/patient-files/actions';
import { PatientFileData } from '../../redux/patient-files/types';
import { RootState } from '../../redux/types';
import { Visit } from '../../redux/visits/types';
import { OkCancelDialog } from '../core/OkCancelDialog';
import { Tag } from '../core/Tag';
import AddFileDialog from '../file-upload/AddFileDialog';
import { PatientFileDialog } from '../visit/dialogs/PatientFileDialog';
import { PatientFileDetails } from './PatientFileDetails';
import styles from './PatientFiles.module.scss';

export interface OwnProps {
  visits: AsyncData<Visit[]>;
  patientId: string;
  patient?: Patient;
  visitCode?: string;
}

export interface StateProps {
  files: AsyncData<PatientFileData[]>;
  userAccountId: string;
}

export interface DispatchProps {
  actions: {
    fetchPatientFiles: (patientId: string, page?: number) => void;
    addPatientFile: (patientId: string, file: PatientFileData) => void;
    approvePatientFile: (patientId: string, file: PatientFileData) => void;
    deletePatientFiles: (files: PatientFileData[], patientId: string) => void;
    releasePatientFiles: (files: PatientFileData[], patientId: string) => void;
    approvePatientFiles: (files: PatientFileData[], patientId: string) => void;
    updatePatientFiles: (files: PatientFileData[], patientId: string) => void;
    toast: (params: ToastParams) => void;
  };
}

type PatientFilesProps = OwnProps & StateProps & DispatchProps;

export interface PatientFilesState {
  textFilter?: string;
  columns: IColumn[];
  selectionDetails: string;
  actionName?: 'Fetching' | 'Deleting' | 'Sending' | 'Refreshing' | 'Updating' | 'Releasing';
  showPanel?: boolean;
  activeFileId?: string;
  intervalId?: any;
  isDragging?: boolean;
  isAddFileDialogOpen: boolean;
  fileToAdd?: File;
}

export class Component extends React.Component<PatientFilesProps, PatientFilesState> {
  private selection: Selection;
  private fileInput: HTMLInputElement | null = null;

  constructor(props: PatientFilesProps) {
    super(props);
    this.state = {
      columns: this.buildColumns(),
      selectionDetails: this.getSelectionDetails(),
      actionName: 'Fetching',
      showPanel: true,
      isAddFileDialogOpen: false,
    };

    this.selection = new Selection({
      onSelectionChanged: () => {
        this.setState({
          selectionDetails: this.getSelectionDetails(),
        });

        /**
         * Implement a tri-state switch for showPanel where only one value is true.
         * By default, the side panel will open the first time an item is selected.
         *
         * If the user then selects an additional item, we hide the panel, because, by
         * design, it can only display information for one of them. However, now it's
         * unclear if the user would want the panel to come back if they deselected
         * back down to one item. So, instead, we use a value of undefined to represent
         * that the panel should stay hidden until the user deselects all the checkboxes,
         * at which point it should be allowed to reappear when the next checkbox is
         * checked.
         *
         * Closing the panel with the X button will set the switch to false, disabling
         * this behavior and keeping the panel hidden until it is manually reopened by
         * clicking on a thumbnail.
         *
         * The intent here is to show the panel more consistently, but ensure it doesn't
         * get in the way and disrupt power users who are accustomed to it being hidden.
         * To this end, if a user doesn't want it to appear, they only have to close it
         * once. If they change their mind, they do what they've always done and click
         * the thumbnail. - JG, 9/2022
         */

        // If the user has selected 2+ items and hasn't manually closed the side panel,
        // set showPanel to a falsy value, but one that is distinct from false (manually closed).
        if (this.selection.count > 1 && this.state.showPanel !== false) {
          this.setState({
            showPanel: undefined,
          });
        }

        // If the user deselects all items and the panel still hasn't been manually closed,
        // allow it to reopen the next time an item is selected.
        if (this.selection.count === 0 && this.state.showPanel === undefined) {
          this.setState({
            showPanel: true,
          });
        }
      },
    });
  }

  public componentDidMount() {
    if (this.props.patientId) {
      this.setState({ actionName: 'Fetching' });
      this.props.actions.fetchPatientFiles(this.props.patientId);
    }
  }

  public componentDidUpdate(prevProps: PatientFilesProps) {
    if (prevProps.patientId !== this.props.patientId && this.props.files && !this.props.files.isFetching) {
      this.setState({ actionName: 'Fetching' });
      this.props.actions.fetchPatientFiles(this.props.patientId);
    }
    if (prevProps.files && prevProps.files.isFetching && this.props.files && !this.props.files.isFetching) {
      this.selection.setAllSelected(false);
    }
  }

  public render(): JSX.Element | null {
    const { files, patient } = this.props;
    const { selectionDetails, columns, actionName } = this.state;

    if (!files) {
      return null;
    }

    if (files.error) {
      return (
        <div style={{ marginTop: 20 }}>
          <MessageBar messageBarType={MessageBarType.error}>{files.error}</MessageBar>
        </div>
      );
    }

    if (this.props.visitCode) {
      if (files.isFetching && actionName === 'Fetching') {
        return <Spinner />;
      }

      if (!files.data) {
        return <MessageBar messageBarType={MessageBarType.error}>Couldn&apos;t retrieve patient files for this visit.</MessageBar>;
      }

      const data = files.data.filter((i) => i.visit_code === this.props.visitCode);

      if (data.length === 0) {
        return <MessageBar messageBarType={MessageBarType.info}>No files for this visit.</MessageBar>;
      } else {
        return (
          <div style={{ display: 'block', marginTop: 5, marginBottom: 5 }}>
            {data.map((file: PatientFileData, index: number) => {
              return this.renderThumbnail(file, true, index);
            })}
          </div>
        );
      }
    } else {
      return (
        <div style={{ marginTop: 10 }}>
          <div className="flex flex-row">
            <div style={{ flex: 1, width: 0 }}>
              <SearchBox placeholder="Filter by file type, visit code or description" onChange={this.onChangeText} iconProps={{ iconName: 'Filter' }} style={{ width: '80%' }} />
              {files.isFetching && <Spinner style={{ marginTop: 8, marginLeft: 8, display: '-webkit-box' }} labelPosition="right" label={this.getActionDetails()} />}
            </div>
            <div style={{ flex: 1, width: 0, textAlign: 'right' }}>
              <div className="flex justify-end" style={{ height: 32 }}>
                <div className="flex">
                  <input
                    value={undefined}
                    ref={(f) => (this.fileInput = f)}
                    type="file"
                    accept="image/*, application/pdf"
                    style={{ display: 'none' }}
                    onChange={this.onFileChange}
                    onClick={() => (this.fileInput!.value = '')}
                  />
                  <CommandBarButton style={{ marginLeft: 10 }} onClick={() => this.fileInput!.click()} iconProps={{ iconName: 'Add' }} text="Add file" />
                </div>
                <CommandBarButton
                  style={{ marginLeft: 10 }}
                  onClick={() => patient && this.OnClickGrantFilesAccess(patient)}
                  disabled={!patient || patient.mainPatientOfAccount}
                  iconProps={{ iconName: 'ConnectContacts' }}
                  text="Grant files access"
                />
                <CommandBarButton
                  style={{ marginLeft: 10 }}
                  onClick={() => this.patientFileAction('Releasing')}
                  disabled={this.getEligibleSelectedFiles('Releasing').length === 0}
                  iconProps={{ iconName: 'Send' }}
                  text="Send to Patient"
                />
                <CommandBarButton
                  style={{ marginLeft: 10 }}
                  onClick={() => this.patientFileAction('Deleting')}
                  disabled={this.getEligibleSelectedFiles('Deleting').length === 0}
                  iconProps={{ iconName: 'Delete' }}
                  text="Delete"
                />
              </div>
              <div style={{ marginTop: 10 }}>{selectionDetails}</div>
            </div>
          </div>
          <Dropzone onDrop={this.onDrop} onDragEnter={this.onDragEnter} onDragLeave={this.onDragLeave} noClick>
            {({ getRootProps }) => (
              <div style={{ marginTop: 10, opacity: this.state.isDragging ? 0.3 : 1 }} {...getRootProps()}>
                {this.selection.getSelectedCount() === 1 && this.renderSidePanel()}
                <MarqueeSelection selection={this.selection} isDraggingConstrainedToRoot>
                  <ShimmeredDetailsList
                    items={this.getFilteredItems() || []}
                    compact
                    setKey="set"
                    columns={columns}
                    enableShimmer={files.isFetching && actionName === 'Fetching'}
                    shimmerLines={8}
                    selection={this.selection}
                    selectionPreservedOnEmptyClick
                  />
                </MarqueeSelection>
                {files.data && files.data.length === 0 && !files.isFetching && <MessageBar messageBarType={MessageBarType.info}>No files for this patient exist.</MessageBar>}
              </div>
            )}
          </Dropzone>
          {this.state.isAddFileDialogOpen && (
            <AddFileDialog
              visits={this.props.visits}
              userAccountId={this.props.userAccountId}
              patientId={this.props.patientId}
              initialFile={this.state.fileToAdd}
              onSuccess={() =>
                this.setState({ isAddFileDialogOpen: false, fileToAdd: undefined, actionName: 'Fetching' }, () => this.props.actions.fetchPatientFiles(this.props.patientId))
              }
              onCancel={() => this.setState({ isAddFileDialogOpen: false, fileToAdd: undefined })}
            />
          )}
        </div>
      );
    }
  }

  private onFileChange = (ev: React.SyntheticEvent<HTMLInputElement>) => {
    const files = ev.currentTarget.files;
    if (!files) {
      return;
    }
    this.onDrop(files as any);
  };

  private onDrop = (files: File[]) => {
    this.onAddFiles(files);
    this.setState({ isDragging: false });
  };

  private onDragEnter = () => {
    this.setState({ isDragging: true });
  };

  private onDragLeave = () => {
    this.setState({ isDragging: false });
  };

  private onAddFiles = (files?: File[]) => {
    this.setState({ fileToAdd: files?.[0], isAddFileDialogOpen: true });
  };

  private OnClickGrantFilesAccess = (patient: Patient) => {
    const MINIMUM_PATIENT_AGE_WHEN_CONSENT_REQUIRED_FOR_FILES_ACCESS = 11;
    const permittedGrants = ['ALL', 'download'];
    const { patientId, ownerAccountId, ownerAccountName, fullName, dateOfBirth } = patient;

    if (age(dateOfBirth) < MINIMUM_PATIENT_AGE_WHEN_CONSENT_REQUIRED_FOR_FILES_ACCESS) {
      return InfoDialog.show({
        title: 'Grant files access',
        subText: (
          <>
            <p>
              <span style={{ fontWeight: 600, color: 'unset' }}>{`${fullName}`}</span> is below the age required for requesting consent to grant files.
            </p>
            <br />
            <p>Patient must be at minimum {MINIMUM_PATIENT_AGE_WHEN_CONSENT_REQUIRED_FOR_FILES_ACCESS} years old to request consent to sharing files access to Account Owner.</p>
            <br />
            <p>
              If <span style={{ fontWeight: 600, color: 'unset' }}>{`${ownerAccountName}`}</span> (Account owner) does not have access to files for{' '}
              <span style={{ fontWeight: 600, color: 'unset' }}>{`${fullName}`}</span> please open a ProdOps ticket.
            </p>
          </>
        ),
      });
    }

    HealAPI.fetchPatientFilesGrants(patientId).then((response) => {
      if (!response.ok) {
        return this.props.actions.toast({
          type: 'error',
          title: 'Error',
          content: "The patient's current file permissions could not be returned, please try again later",
        });
      }

      const grants = response.data?.data.files;
      const accountOwnerGrants = grants?.[`${ownerAccountId}`];
      const accountOwnerHasAccessToPatientFiles = accountOwnerGrants?.some((grant) => permittedGrants.includes(grant));

      if (accountOwnerHasAccessToPatientFiles) {
        return InfoDialog.show({
          title: 'Grant files access',
          subText: (
            <>
              <span style={{ fontWeight: 600, color: 'unset' }}>{`${ownerAccountName}`}</span> (Account Owner) currently has access to files for{' '}
              <span style={{ fontWeight: 600, color: 'unset' }}>{fullName}</span>. If you need to remove files access please open a ProdOps ticket.
            </>
          ),
        });
      }

      return this.showGrantFilesDialog(patient);
    });
  };

  private showGrantFilesDialog = (patient: Patient) => {
    const { ownerAccountName, fullName, dateOfBirth, email, phone } = patient;
    OkCancelDialog.show({
      title: 'Grant files access',
      subText: (
        <>
          <div>
            <span style={{ fontWeight: 600, color: 'unset' }}>{`${ownerAccountName}`}</span> (Account owner) will be granted access to files for{' '}
            <span style={{ fontWeight: 600, color: 'unset' }}>{fullName}</span>.
          </div>
          <div style={{ margin: '20px 0' }}>
            You must verify the following patient details:
            <table className={styles.patientDetails}>
              <tbody>
                <tr>
                  <td>Full Name</td>
                  <td>{fullName}</td>
                </tr>
                <tr>
                  <td>Date of Birth</td>
                  <td>{dateOfBirth}</td>
                </tr>
                <tr>
                  <td>Email</td>
                  <td>{email}</td>
                </tr>
                <tr>
                  <td>Phone</td>
                  <td>{phone}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 20, marginBottom: 10 }}>Are you sure you wish to proceed?</div>
        </>
      ),
      warning: `Please confirm you have acquired and recorded consent from ${patient.fullName} before proceeding.`,
      okText: 'Proceed',
      onOk: () => {
        HealAPI.grantAccountOwnerAccessToPatientFiles(patient.ownerAccountId, patient.patientId).then((response) => {
          if (response.ok) {
            this.props.actions.toast({
              type: 'success',
              title: 'Success',
              content: 'Account owner granted access to patient files.',
            });
          } else {
            this.props.actions.toast({
              type: 'error',
              title: 'Error',
              content: 'The account owner could not be granted access to patient files, please try again later',
            });
          }
        });
      },
    });
  };

  private renderSidePanel(): JSX.Element {
    const { showPanel } = this.state;
    const { files, patientId, visits } = this.props;
    const activeFile = this.selection.getSelection()[0] as PatientFileData;

    return (
      <Panel isOpen={showPanel} onDismiss={() => this.setState({ showPanel: false })} isBlocking={false} type={PanelType.medium}>
        <PatientFileDetails
          visits={visits}
          file={activeFile}
          patientId={patientId}
          handleKeyPress={this.handleKeyPress}
          patientFileAction={this.patientFileAction}
          isSaving={files && files.isFetching}
          onClosed={() => this.setState({ showPanel: false })}
        />
      </Panel>
    );
  }

  private renderThumbnail(file: PatientFileData, small: boolean, key?: number): JSX.Element {
    const status = this.getStatus(file);

    return (
      <div
        key={key}
        className={small && file._id === this.state.activeFileId ? 'active-file' : 'file'}
        style={{
          width: 50,
          height: 50,
          backgroundImage: `url('${file.thumbnail_url}')`,
          backgroundSize: 'cover',
          position: 'relative',
          cursor: 'pointer',
          display: 'inline-flex',
          marginRight: 5,
          marginBottom: 5,
        }}
        onClick={() => (small ? this.onOpenDialog(file._id) : this.onOpenSidePanel())}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            background: 'rgba(255,255,255,0.5)',
          }}
        />
        {small && (
          <Icon
            iconName={status.iconName}
            style={{
              position: 'absolute',
              fontSize: 16,
              bottom: 6,
              right: 6,
              color: status.iconColor,
            }}
          />
        )}
      </div>
    );
  }

  private handleKeyPress = (key: string) => {
    const currentKey = this.selection.getSelectedIndices()[0];
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      this.selection.setIndexSelected(currentKey, false, false);
    }

    switch (key) {
      case 'ArrowDown':
        this.selection.setIndexSelected(currentKey + 1, true, false);
        break;
      case 'ArrowUp':
        this.selection.setIndexSelected(currentKey - 1, true, false);
        break;
      default:
        break;
    }
  };

  private onOpenSidePanel = () => {
    this.setState({ showPanel: true });
  };

  private onOpenDialog(activeFileId: string) {
    this.setState({ activeFileId });

    const { patientId, files, visitCode } = this.props;

    if (files && files.data) {
      PatientFileDialog.show({
        files: files.data.filter((i) => i.visit_code === visitCode),
        activeFileId,
        patientId,
        patientFileAction: this.patientFileAction,
        updateActiveFile: this.updateActiveFile,
      });
    }
  }

  private onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    this.setState({ textFilter: newValue });
  };

  private getSelectionDetails(): string {
    const selectionCount = this.selection ? this.selection.getSelectedCount() : 0;
    switch (selectionCount) {
      case 0:
        return 'No files selected';
      case 1:
        return '1 file selected';
      default:
        return `${selectionCount} files selected`;
    }
  }

  private getActionDetails(): string {
    const { actionName } = this.state;
    if (!actionName) {
      return '';
    }
    return actionName + (actionName !== 'Fetching' && actionName !== 'Refreshing' ? ' ' + String(this.getEligibleSelectedFiles(actionName).length) : '') + ' File(s)';
  }

  private getFilteredItems(): PatientFileData[] | null {
    const { files } = this.props;
    const { textFilter } = this.state;

    if (!files || !files.data) {
      return null;
    }

    const filteredData = files.data.filter((i) => i.document_type !== 'insuranceCard');

    return textFilter
      ? filteredData.filter(
          (i) =>
            (i.visit_code && i.visit_code.toLowerCase().indexOf(textFilter.toLowerCase()) > -1) ||
            (i.document_type && i.document_type.toLowerCase().indexOf(textFilter.toLowerCase()) > -1) ||
            (i.description && i.description.toLowerCase().indexOf(textFilter.toLowerCase()) > -1)
        )
      : filteredData;
  }

  private getEligibleSelectedFiles(action?: 'Deleting' | 'Sending' | 'Updating' | 'Releasing') {
    if (action === 'Releasing') {
      return (this.selection.getSelection() as PatientFileData[]).filter((i) => !i.released_at && i.context === 'unreleased' && i.download_url);
    }
    if (action === 'Sending') {
      return (this.selection.getSelection() as PatientFileData[]).filter((i) => !i.approved_on && i.download_url && i.document_type !== 'labResult');
    }
    return this.selection.getSelection() as PatientFileData[];
  }

  private getStatus(file: PatientFileData): { iconName: string; iconColor: string; copy: string } {
    let status = { iconName: 'Brightness', iconColor: '#147AD4', copy: 'New' };

    if (file.download_url === null) {
      status = {
        iconName: 'Clock',
        iconColor: 'orange',
        copy: 'Queued ',
      };
    }

    return status;
  }

  private updateActiveFile = (activeFileIndex: number | null) => {
    if (activeFileIndex === null) {
      this.setState({ activeFileId: undefined });
    } else if (this.props.files && this.props.files.data && this.props.visitCode) {
      const activeFile = this.props.files.data.filter((i) => i.visit_code === this.props.visitCode)[activeFileIndex];
      if (activeFile) {
        this.setState({ activeFileId: activeFile._id });
      }
    }
  };

  private patientFileAction = (actionName: 'Sending' | 'Deleting' | 'Updating' | 'Releasing', file?: PatientFileData, description?: string, visitCode?: string) => {
    const { patientId } = this.props;

    if (file && actionName === 'Updating' && visitCode) {
      file.visit_code = visitCode;
    }

    if (file && actionName === 'Updating' && description) {
      file.description = description;
    }

    const filesToUpload = file ? [file] : this.getEligibleSelectedFiles(actionName);

    this.setState({ actionName });

    if (filesToUpload.length > 0) {
      if (actionName === 'Sending') {
        this.props.actions.approvePatientFiles(filesToUpload, patientId);
      }
      if (actionName === 'Updating') {
        this.props.actions.updatePatientFiles(filesToUpload, patientId);
      }
      if (actionName === 'Deleting') {
        OkCancelDialog.show({
          title: 'Delete files',
          subText: filesToUpload.length + ' file(s) will be deleted. Are you sure?',
          onOk: () => this.props.actions.deletePatientFiles(filesToUpload, patientId),
        });
      }
      if (actionName === 'Releasing') {
        OkCancelDialog.show({
          title: 'Release files',
          subText: filesToUpload.length + ' file(s) will be released to patient. Are you sure?',
          onOk: () => this.props.actions.releasePatientFiles(filesToUpload, patientId),
        });
      }
    }
  };

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'preview',
        name: 'Preview',
        fieldName: 'thumbnailUrl',
        minWidth: 80,
        maxWidth: 80,
        onRender: (item: PatientFileData) => {
          return this.renderThumbnail(item, false);
        },
      },
      {
        key: 'type',
        name: 'File Type',
        fieldName: 'document_type',
        minWidth: 100,
        maxWidth: 100,
        onRender: (item: PatientFileData) => {
          const document_type = DOCUMENT_TYPE.find((t) => t.key === item.document_type);
          if (item.minor_sensitive) {
            return (
              <div>
                <div>{document_type ? document_type.text : item.document_type}</div>
                <Tag text="sensitive" className="label-as-badge red-badge" />
              </div>
            );
          }
          return document_type ? document_type.text : item.document_type;
        },
      },
      {
        key: 'visit_code',
        name: 'Visit code',
        fieldName: 'visit_code',
        minWidth: 80,
        maxWidth: 80,
      },
      {
        key: 'uploadedAt',
        name: 'Uploaded',
        fieldName: 'uploaded_at',
        minWidth: 90,
        maxWidth: 90,
        onRender: (item: PatientFileData) => {
          return item.download_url ? (
            <div className="flex">
              <i className="ms-font-m ms-Icon ms-Icon--Completed" style={{ color: 'green', marginTop: 1, marginRight: 4 }} />
              {moment(item.uploaded_at || item._created).format('L')}
            </div>
          ) : (
            <div className="flex">
              <i className="ms-font-m ms-Icon ms-Icon--Clock" style={{ color: 'orange', marginTop: 1, marginRight: 4 }} />
              Queued
            </div>
          );
        },
      },
      {
        key: 'releasedAt',
        name: 'Sent to Patient',
        fieldName: 'released_at',
        minWidth: 120,
        maxWidth: 120,
        onRender: (item: PatientFileData) => {
          // Any file could be released and not have released_at attribute
          // Unreleased files may have the legacy attribute {context: "unreleased"} or the newer {unreleased: true}
          const itemIsReleased = item.context !== 'unreleased' || !item.unreleased;

          return itemIsReleased ? (
            <div className="flex">
              <i className="ms-font-m ms-Icon ms-Icon--Completed" style={{ color: 'green', marginTop: 1, marginRight: 4 }} />
              {item.released_at ? moment(item.released_at).format('L') : 'Unknown date'}
            </div>
          ) : null;
        },
      },
      {
        key: 'description',
        name: 'Description',
        fieldName: 'description',
        minWidth: 80,
        maxWidth: 150,
        isMultiline: true,
      },
    ];
  }
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  return {
    files: state.patientFiles.data[ownProps.patientId],
    userAccountId: state.auth.userAccountId,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
  return {
    actions: bindActionCreators(
      {
        fetchPatientFiles,
        addPatientFile,
        approvePatientFile,
        deletePatientFiles,
        releasePatientFiles,
        approvePatientFiles,
        updatePatientFiles,
        toast,
      },
      dispatch
    ),
  };
};

export const PatientFiles = connect<StateProps, DispatchProps, OwnProps, RootState>(mapStateToProps, mapDispatchToProps)(Component);
